import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import QualityComponentInterface from '@app/Interface/QualityComponentInterface';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import TabulatorReferenceInterface from '@app/Interface/TabulatorReferenceInterface';
import { Title, Flex, FlexItem, Button, Card, CardBody, CardTitle, TextContent, Text } from '@patternfly/react-core';
import { DownloadIcon, AngleLeftIcon } from '@patternfly/react-icons';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import moment from 'moment-timezone';
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface EStatementDetailsReportProps {
    selectedDate: moment.Moment;
    clearSelectedDate: Function;
    pageId: number,
    reportType: string;
}

const EStatementDetailsReport: React.FC<EStatementDetailsReportProps> = ({selectedDate, clearSelectedDate, pageId, reportType}) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    // Calculate the start date of this statement
    const statementStart: moment.Moment = moment(selectedDate).subtract(1, 'weeks');

    const [qualityTableRef, setQualityTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [componentsTableRef, setComponentsTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [reportData, setReportData] = React.useState<QualityComponentInterface[]>([]);
    const [loading, setLoading] = React.useState(true);


    const qualityTableCallback = React.useCallback(node => {
        setQualityTableRef({current: node});
    }, []);

    const componentsTableCallback = React.useCallback(node => {
        setComponentsTableRef({current: node});
    }, []);

    const leucTestThreshold = Number(process.env.THRESHOLD_LEUC_TEST) ?? 350;
    const leucTestCompareType = String(process.env.THRESHOLD_LEUC_TEST_COMPARE_TYPE) ?? "greater";

    const piTestThreshold = Number(process.env.THRESHOLD_PI_TEST) ?? 50;
    const piTestCompareType = String(process.env.THRESHOLD_PI_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const waterTestThreshold = Number(process.env.THRESHOLD_WATER_TEST) ?? 524;
    const waterTestCompareType = String(process.env.THRESHOLD_WATER_TEST_COMPARE_TYPE) ?? "lesserOrEqual";

    const rawTestThreshold = Number(process.env.THRESHOLD_RAW_TEST) ?? 15;
    const rawTestCompareType = String(process.env.THRESHOLD_RAW_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const qualityTabulatorColumns = [
        { title: 'Date', field: 'PICKUP_DATE', formatter: 'datetime', formatterParams: {
            outputFormat: 'MMM D'
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Tank', field: 'TANK_NUMBER', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Lbs', field: 'POUNDS', formatter: 'money', formatterParams: {
            precision: false
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Raw', field: 'RAW_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: rawTestThreshold, compareType: rawTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'PI', field: 'PI_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: piTestThreshold, compareType: piTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SCC', field: 'LEUC_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: leucTestThreshold, compareType: leucTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Water', field: 'WATER_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: waterTestThreshold, compareType: waterTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'AB', field: 'AB_TEST', formatter: TabulatorFormatters.abTestFormatter, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SED', field: 'SED_TEST', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'LPC', field: 'LPC_TEST', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];
    const componentsTabulatorColumns = [
        { title: 'Date', field: 'PICKUP_DATE', formatter: 'datetime', formatterParams: {
            outputFormat: 'MMM D'
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Tank', field: 'TANK_NUMBER', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Lbs', field: 'POUNDS', formatter: 'money', formatterParams: {
            precision: false
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Fat%', field: 'BFAT_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: {flagField: 'BFAT_FLAG'}, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Pro%', field: 'PROT_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: {flagField: 'PROT_FLAG'}, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'OSL%', field: 'OSOL_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: {flagField: 'OSOL_FLAG'}, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SNF%', field: 'SNF_TEST', formatter: 'money', formatterParams: {
            precision: false
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SCC', field: 'LEUC_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: {flagField: 'LEUC_FLAG'}, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'M.U.N.', field: 'MUN_TEST', formatter: TabulatorFormatters.dailyComponentsSubstitutionFormatter, formatterParams: {flagField: 'MUN_FLAG'}, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Temp', field: 'TEMPERATURE', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'LPC', field: 'LPC_TEST', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        setLoading(true);
        if (userState.user?.currentFpuId > 0) {
            // Get report data
            const startDate : string = statementStart.format(moment.HTML5_FMT.DATE);
            const endDate : string = selectedDate.format(moment.HTML5_FMT.DATE);
            ReportDataHelper.getEStatementsData(userState.user.currentFpuId, pageId, startDate, endDate)
                .then(componentsRows => setReportData(componentsRows))
                .catch(err => setReportData([]))
                .finally(() => setLoading(false));
            
        } else {
            setReportData([]);
            setLoading(false);
        }
        
    }, [pageId, userState.user.id, userState.user.currentFpuId]);

    const downloadReportCSV = (isQuality: boolean = false) => {

        const ref = isQuality ? qualityTableRef : componentsTableRef;

        if (ref?.current) {
            const tabulator: TabulatorReferenceInterface = ref.current;
            const tableName = isQuality ? 'quality' : 'components';
            const date = selectedDate.format(moment.HTML5_FMT.DATE);
            const filename = `${reportType}-${tableName}-${date}-export.csv`;
            tabulator.table.download('csv', filename);

        } else {
            console.log('Unable to download report CSV');
        }
    };

    const getTable = (isQuality: boolean = false) => {
        const columns = isQuality ? qualityTabulatorColumns : componentsTabulatorColumns;
        const ref = isQuality ? qualityTableCallback : componentsTableCallback;

        return (
            <>
                {
                    reportData.length === 0
                    ? <p><em>No report data to show for the selected producer</em></p>
                    : <>
                        <Flex>
                            <FlexItem align={{ default: 'alignRight' }}>
                                <Button className="pf-u-float-right" icon={<DownloadIcon/>} onClick={e => downloadReportCSV(isQuality)}>
                                    Download CSV
                                </Button>
                            </FlexItem>
                        </Flex>
                        <SCTabulator
                            tableRef={ref}
                            data={reportData}
                            columns={columns}
                            layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                            options={{
                                ...options('nda-details-sc')
                            }}
                        />
                    </>
                }
            </>
        );
    };

    const getStatementPeriodText = () => {
        return `${statementStart.format('dddd, MMMM DD, YYYY')} - ${selectedDate.format('dddd, MMMM DD, YYYY')}`;
    };

    const pdfLink = ReportDataHelper.getReportPdfUrl(
        'e-statements',
        userState.user.currentFpuId,
        undefined,
        undefined,
        selectedDate.format('MM-DD-YYYY')
    );

    return (
        <>
            <Button
                onClick={e => clearSelectedDate()}
                icon={<AngleLeftIcon />}
            >
                Return to E-Statements List
            </Button>
            <Title headingLevel="h2">Statement Period</Title>
            <Title headingLevel="h3">{getStatementPeriodText()}</Title>
            {!loading && reportData.length !== 0 &&
                <Flex>
                    <FlexItem align={{ default: 'alignRight' }}>
                        <Button className="pf-u-float-right" icon={<DownloadIcon/>} component="a" target="_blank" href={pdfLink}>
                            Download PDF
                        </Button>
                    </FlexItem>
                </Flex>
            }
            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Latest Quality</Title>
                </CardTitle>
                <CardBody>
                    {
                        loading
                        ? <ItemLoader />
                        : getTable(true)
                    }
                </CardBody>
            </Card>

            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Latest Components</Title>
                </CardTitle>
                <CardBody>
                    {
                        loading
                        ? <ItemLoader />
                        : getTable()
                    }
                </CardBody>
            </Card>
        </>
    );
};

export default EStatementDetailsReport;